import React, { Fragment } from 'react';
import { Link }            from 'react-router-dom';
import classNames          from 'classnames';
import Alert               from '../../shared/alert';
import LoaderButton        from '../../shared/loader-button/loader-button.jsx';
import LanguageSelector    from '../../shared/language-selector/language-selector.jsx';
import LocaleString        from '../../shared/locale-string';
import PasswordInput       from '../../shared/password-input/password-input.jsx';
import describedByString   from '../../../utils/describedby-string';

import './login.scss';

class Login extends React.Component {
    resetFocus = false;
    state = {
        username: '',
        password: ''
    };

    componentWillUnmount() {
        this.props.dismissAllErrors();
    }

    UNSAFE_componentWillUpdate( nextProps ) {//eslint-disable-line
        const {
            preActivationLoginAttemptFlag,
            routeTo,
            errors: {
                length: newErrorsLength
            }
        } = nextProps;

        const oldErrorsLength = this.props.errors.length;
        this.resetFocus = ( newErrorsLength > oldErrorsLength );

        if ( preActivationLoginAttemptFlag ) {
            routeTo( '/activation' );
        }
    }

    componentDidUpdate() {
        // on error, move focus to first input
        if ( this.resetFocus ) {
            document.getElementById( 'username' ).focus();
            this.resetFocus = false;
        }
    }

    handleInputChange = event => {
        const name = event.target.name;
        this.setState( { [ name ]: event.target.value } );
    };

    handleLocaleChange = ( val ) => {
        const locale = val.locale.code;
        const fetchBundle = this.props.bundleList[ locale ];

        if ( !fetchBundle ) {
            this.props.fetchBundleData( locale );
        } else {
            this.props.setUserData( val );
        }
    };

    onLoginSubmit = () => {
        const { username, password } = this.state;
        const { login, dismissAllErrors } = this.props;

        dismissAllErrors();
        login( username, password );
    };

    checkEnter = event => {
        const enterPressed = event.charCode === 13;
        const { username, password } = this.state;

        if ( enterPressed && username.length && password.length ) {
            this.onLoginSubmit();
        }
    };

    render() {
        const { username, password } = this.state;
        const {
            dismissError,
            done,
            locale,
            localeOptions,
            errors,
            loggingIn
        } = this.props;

        const hasError = errors.length !== 0;
        const formClasses = classNames( {
            'narrow-form': true,
            'login-error': hasError
        } );

        const languageMenu = localeOptions.length > 1 && <Fragment>
            <label htmlFor="language" key="languageSelectorLabel">
                <LocaleString code="login.input.languageLabel" />
            </label>
            <LanguageSelector
                tabIndex="1"
                id="language"
                key="languageSelectorMenu"
                options={ localeOptions }
                selectedLocale={ locale }
                onClick={ ( event ) => this.handleLocaleChange( event ) }
            /></Fragment>;

        const describedByErrors = describedByString( errors, 'error' );

        return (
            <div className="login">
                <div className={ formClasses } role="form">

                    <h1 className="sr-only">Login</h1>
                    <div aria-describedby={ describedByErrors }>
                        { languageMenu }

                        <Link to="/forgot-user-id" className="input-help">
                            <LocaleString code="login.input.forgotUserIdLabel" />
                        </Link>
                        <label htmlFor="username">
                            <LocaleString code="common.input.usernameLabel" />
                        </label>

                        <input
                            id="username"
                            type="text"
                            name="username"
                            onChange={ this.handleInputChange }
                            onKeyPress={ this.checkEnter }
                            required={ true }
                            tabIndex="2"
                        />

                        <PasswordInput
                            id="password"
                            label={ <LocaleString code="login.input.passwordLabel" /> }
                            name="password"
                            value={ password }
                            onChange={ this.handleInputChange }
                            onKeyPress={ this.checkEnter }
                            tabIndex="3"
                            helpComponent={
                                <Link to="/forgot-password" className="input-help">
                                    <LocaleString code="login.input.forgotPasswordLabel" />
                                </Link>
                            }
                        />
                    </div>

                    {
                        hasError && errors.map( ( error, index ) =>
                            <Alert
                                type={ 'error' }
                                onDismiss={ () => dismissError( error ) }
                                id={ `error${index}` }
                                key={ index }>
                                <p>{ error.responseMessage || error.message }</p>
                            </Alert>
                        )
                    }

                    <p className="activation-link">
                        <LocaleString code="login.input.noPasswordLabel" />
                        {' '}
                        <Link to="/activation"><LocaleString code="login.link.activateLabel" /></Link>
                    </p>

                    <LoaderButton
                        id="btnLogin"
                        busy={ loggingIn }
                        onClick={ this.onLoginSubmit }
                        disabled={ !username.length || !password.length }
                        done={ done }>
                        <LocaleString code="login.button.submitLabel" />
                    </LoaderButton>

                </div>
            </div>
        );
    }
}

export default Login;

